import type { EconSplit, EconomyDepth, ScoutMapAgg } from '../types'

// The economy beyond "you win 62% of full buys".
//
// Three questions the headline never answered: does forcing ever convert, is a lost round saved or
// thrown away, and does losing an eco cost the round after it too. All three came out of data the
// fold was already reading and discarding.
//
// Every figure shows what it came from. That isn't decoration — a force conversion off three rounds
// and one off thirty look identical as a percentage, and the first one is noise. The rule this
// codebase already follows on the veto sheet is to label thin data rather than dress it up, so a
// low-confidence number is drawn dim with its round count next to it and never as a bare percent.

const TONE: Record<string, string> = {
  high: 'var(--text)',
  medium: 'var(--text-dim)',
  low: 'var(--text-faint)',
  none: 'var(--text-faint)',
}

/** One figure, honest about its sample. `null` pct means it never happened, which is not 0%. */
function Figure({ label, s, hint }: { label: string; s: EconSplit; hint: string }) {
  const thin = s.confidence === 'low' || s.confidence === 'none'
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-text-faint">{label}</div>
      <div className="font-display text-xl tabular-nums" style={{ color: TONE[s.confidence] }}>
        {s.pct === null ? '—' : `${s.pct}%`}
      </div>
      <div className="text-[11px] text-text-faint">
        {s.played === 0 ? (
          'never happened'
        ) : (
          <>
            {s.won}/{s.played} rounds
            {thin && <span className="text-loss"> · too few to trust</span>}
          </>
        )}
      </div>
      <div className="mt-0.5 text-[10px] leading-snug text-text-faint">{hint}</div>
    </div>
  )
}

export function Economy({ maps, who }: { maps: ScoutMapAgg[]; who: string }) {
  // Merged across maps here rather than in the fold: the fold keeps it per map because a coach
  // picking a veto wants Nuke's economy, and a coach reviewing a season wants all of it. Adding
  // counts is safe; averaging the percentages would not be.
  const parts = maps.map((m) => m.econDepth).filter((d): d is EconomyDepth => !!d)
  if (!parts.length) return null

  const add = (pick: (d: EconomyDepth) => EconSplit): EconSplit => {
    const won = parts.reduce((n, d) => n + pick(d).won, 0)
    const played = parts.reduce((n, d) => n + pick(d).played, 0)
    return {
      won,
      played,
      pct: played ? Math.round((100 * won) / played) : null,
      confidence: played === 0 ? 'none' : played >= 10 ? 'high' : played >= 4 ? 'medium' : 'low',
    }
  }

  const rounds = parts.reduce((n, d) => n + d.rounds, 0)
  if (!rounds) return null

  return (
    <div className="mt-4">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Figure
          label="Force conversion"
          s={add((d) => d.forceConversion)}
          hint={`How often ${who} force buy and win it.`}
        />
        <Figure
          label="After a lost eco"
          s={add((d) => d.afterLostEco)}
          hint="The round after — a thrown-away eco costs two, and nothing else attributes the second."
        />
        <Figure
          label="After a lost full buy"
          s={add((d) => d.afterLostFull)}
          hint="Whether a bad round becomes a bad half."
        />
        <Figure
          label="Saved after a loss"
          s={add((d) => d.savedAfterLoss)}
          hint="Ecos rather than forces on a broken economy. Reported, not scored — teams differ here on purpose."
        />
      </div>
      <p className="mt-3 text-[11px] text-text-faint">
        Over {rounds} round{rounds === 1 ? '' : 's'} with a recorded buy. Rounds either side of a half
        boundary aren't chained together — you swap economies at the switch, so "the round after"
        would be somebody else's.
      </p>
    </div>
  )
}
